import type { ReactNode } from 'react';
import { DemoLock } from './DemoLock';
import { IS_DEMO } from '../../apiBase';

// Consistent top-of-phase layout: every phase shows its run/execution controls
// centered at the top. There used to be a rule under them separating them from the
// results below, and a `divider` prop to suppress it where nothing rendered below;
// the reskin took every hairline off the shell (a slate-200 line on the blueprint
// is a scratch, not a divider), so there is no rule left to suppress.
//
// The gap around the controls belongs here, not to the caller. This used to be a
// fragment of two bare siblings, spaced only by the parent's own `flex flex-col
// gap-6` — an unwritten contract that every phase had to remember, and that Phase 1
// broke silently with nothing to catch it. Owning the gap keeps this component
// whole: it is a single flex child, so the parent's gap still separates it from the
// results below, exactly as before.
//
// AND IT IS WHERE THE DEMO IS HIDDEN. Every phase puts its run buttons in here, so this
// one wrapper draws none of them at all in the demo — and goes on hiding the ones added
// later, which is the half a list of buttons could never do. The demo refuses every call
// that spends (backend/appenv.py), so these were already dead behind DemoLock; a row of
// greyed-out buttons is the demo explaining what it will not do, on a page whose whole job
// is to show what it already did.
//
// The lock stays for the builds that are LOCKED but not the demo — the sandbox, where the
// buttons are shown and refused rather than hidden.
//
// AND IT IS WHERE THE SANDBOX IS LOCKED. Every phase puts its run buttons in here, so
// this one wrapper turns off all of them at once — and goes on turning off the ones
// added later, which is the half a list of buttons could never do.
export function PhaseControls({
  children,
  keepInDemo = false,
}: {
  children: ReactNode;
  keepInDemo?: boolean;
}) {
  // `keepInDemo` is for the publish panel, which is not only run controls: it also carries
  // the public page's LINK and its QR, which is the one thing Phase 7 exists to show and
  // exactly what a demo is for. It does its own demo gating inside (the Update/Unpublish
  // pair goes, the link stays), so the blanket rule here would have thrown away the answer
  // to keep the buttons out — which it did, until this.
  if (IS_DEMO && !keepInDemo) return null;
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-center gap-3">
        <DemoLock>{children}</DemoLock>
      </div>
    </div>
  );
}
